import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { injectsApi, exercisesApi, reportsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import InjectQuickViewModal from '../../components/ems/InjectQuickViewModal';
import InjectDatabasePicker from '../../components/ems/InjectDatabasePicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus,
  Search,
  Filter,
  MoreVertical,
  Upload,
  Activity,
  Send,
  Edit,
  Trash2,
  Target,
  FileText,
  Mail,
  Database
} from 'lucide-react';

export default function InjectsPage() {
  const { isController } = useAuth();
  const [injects, setInjects] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [exerciseFilter, setExerciseFilter] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedInject, setSelectedInject] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Quick View
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewInject, setQuickViewInject] = useState(null);
  
  // Database Picker
  const [dbPickerOpen, setDbPickerOpen] = useState(false);
  const [dbPickerExerciseId, setDbPickerExerciseId] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    inject_time: '',
    method: '',
    dependancy: '',
    recipient: '',
    specific_function: '',
    objective: '',
    reference: '',
    injected_by: '',
    supplemental_information: '',
    key_outcomes: '',
    exercise_id: '',
    priority: 'medium',
    target_roles: [],
    target_users: []
  });
  
  const fileInputRef = useRef(null);

  const exerciseMap = useMemo(() => {
    const map = {};
    exercises.forEach((exercise) => {
      map[exercise.exercise_id] = exercise.name;
    });
    return map;
  }, [exercises]);

  const getScheduledTime = (inject) => {
    const meta = inject?.metadata || {};
    return (
      meta['Time of Inject (UTC)'] ||
      meta['Inject Time'] ||
      meta['Inject Time (UTC)'] ||
      meta.time_of_inject_utc ||
      meta.inject_time ||
      meta.time_of_inject ||
      null
    );
  };

  const getRecipientLabel = (inject) => {
    const meta = inject?.metadata || {};
    return (
      meta.recipient ||
      meta.Recipient ||
      meta['Recipient'] ||
      meta['Inject Category'] ||
      meta.inject_category ||
      inject?.recipient ||
      inject?.category ||
      '-'
    );
  };

  const getInjectIdLabel = (inject) => {
    const meta = inject?.metadata || {};
    return (
      meta['Inject ID'] ||
      meta.inject_id ||
      inject?.inject_number ||
      inject?.inject_id ||
      '-'
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [injectsData, exercisesData] = await Promise.all([
        injectsApi.getAll(),
        exercisesApi.getAll()
      ]);
      setInjects(injectsData);
      setExercises(exercisesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const buildInjectMetadata = (form) => {
    const raw = {
      'Time of Inject (UTC)': form.inject_time,
      'Inject Time': form.inject_time,
      Method: form.method,
      Dependancy: form.dependancy,
      Recipient: form.recipient,
      'Specific Function': form.specific_function,
      Objective: form.objective,
      'Objective ID': form.reference,
      'Inject Information': form.content,
      'Injected By': form.injected_by,
      'Supplemental Information': form.supplemental_information,
      'Key Outcomes': form.key_outcomes,
    };
    return Object.fromEntries(Object.entries(raw).filter(([, v]) => v));
  };

  const handleCreateInject = async (e) => {
    e.preventDefault();
    if (!formData.exercise_id) {
      toast.error('Please select an exercise');
      return;
    }
    setFormLoading(true);
    
    try {
      await injectsApi.create({
        ...formData,
        metadata: buildInjectMetadata(formData),
      });
      toast.success('Inject created');
      setCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateInject = async (e) => {
    e.preventDefault();
    if (!selectedInject) return;
    setFormLoading(true);
    
    try {
      await injectsApi.update(selectedInject.inject_id, {
        title: formData.title,
        content: formData.content,
        priority: formData.priority,
        metadata: buildInjectMetadata(formData),
      });
      toast.success('Inject updated');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteInject = async (injectId) => {
    if (!window.confirm('Are you sure you want to delete this inject?')) return;
    
    try {
      await injectsApi.delete(injectId);
      toast.success('Inject deleted');
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleTriggerInject = async (injectId) => {
    try {
      await injectsApi.trigger(injectId);
      toast.success('Inject played');
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCompleteInject = async (injectId) => {
    try {
      await injectsApi.complete(injectId);
      toast.success('Inject completed');
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !formData.exercise_id) {
      toast.error('Please select both a file and an exercise');
      return;
    }
    
    setFormLoading(true);
    try {
      const result = await injectsApi.bulkImport(formData.exercise_id, file);
      const importedCount = result.injects_created ?? result.created ?? 0;
      const objectivesCount = result.objectives_created ?? 0;
      toast.success(`Imported ${importedCount} injects${objectivesCount ? ` and ${objectivesCount} objectives` : ''}`);
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} rows had errors`);
      }
      setImportDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFormLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      inject_time: '',
      method: '',
      dependancy: '',
      recipient: '',
      specific_function: '',
      objective: '',
      reference: '',
      injected_by: '',
      supplemental_information: '',
      key_outcomes: '',
      exercise_id: '',
      priority: 'medium',
      target_roles: [],
      target_users: []
    });
  };

  const handleEmailReport = async () => {
    setEmailLoading(true);
    try {
      const params = {};
      if (exerciseFilter !== 'all') params.exerciseId = exerciseFilter;
      const result = await reportsApi.emailInjectReport(params);
      toast.success(result.message || 'Report emailed successfully');
    } catch (error) {
      // If email not configured, fall back to PDF download
      if (error.message?.includes('RESEND_API_KEY') || error.message?.includes('Email service not configured')) {
        try {
          const queryParams = new URLSearchParams();
          if (exerciseFilter !== 'all') queryParams.set('exercise_id', exerciseFilter);
          queryParams.set('download_only', 'true');
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const token = sessionStorage.getItem('token');
          const resp = await fetch(`${API_URL}/api/reports/email-pdf?download_only=true`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(exerciseFilter !== 'all' ? { exercise_id: exerciseFilter } : {}),
          });
          if (!resp.ok) throw new Error('Download failed');
          const blob = await resp.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `inject_report_${new Date().toISOString().slice(0,10)}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          toast.success('Report downloaded (email service not configured)');
        } catch (dlErr) {
          toast.error('Failed to download report: ' + dlErr.message);
        }
      } else {
        toast.error(error.message || 'Failed to email report');
      }
    } finally {
      setEmailLoading(false);
    }
  };

  const openQuickView = (inject) => {
    setQuickViewInject(inject);
    setQuickViewOpen(true);
  };

  const formatMeta = (v) => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'string' && v.trim() === '') return '-';
    return String(v);
  };


  const openEditDialog = (inject) => {
    setSelectedInject(inject);
    const meta = inject.metadata || {};
    setFormData({
      title: inject.title || '',
      content: inject.content || '',
      exercise_id: inject.exercise_id || '',
      priority: inject.priority || 'medium',
      inject_time: meta['Time of Inject (UTC)'] || meta['Inject Time'] || meta.time_of_inject_utc || meta.inject_time || '',
      method: meta.Method || meta.method || '',
      dependancy: meta.Dependancy || meta.dependancy || '',
      recipient: meta.Recipient || meta.recipient || '',
      specific_function: meta['Specific Function'] || meta.specific_function || '',
      objective: meta.Objective || meta.objective || '',
      reference: meta['Objective ID'] || meta.objective_id || '',
      injected_by: meta['Injected By'] || meta.injected_by || '',
      supplemental_information: meta['Supplemental Information'] || meta.supplemental_information || '',
      key_outcomes: meta['Key Outcomes'] || meta.key_outcomes || '',
      target_roles: inject.target_roles || [],
      target_users: inject.target_users || [],
    });
    setEditDialogOpen(true);
  };

  const filteredInjects = injects.filter(i => {
    const matchesSearch = i.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         i.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesExercise = exerciseFilter === 'all' || i.exercise_id === exerciseFilter;
    return matchesSearch && matchesExercise;
  });

  const getPriorityClass = (priority) => {
    const classes = {
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  const getStatusClass = (status) => {
    const classes = {
      draft: 'bg-muted0/10 text-muted-foreground',
      ongoing: 'bg-blue-500/10 text-blue-500',
      completed: 'bg-green-500/10 text-green-500'
    };
    return classes[status] || '';
  };

  return (
    <DashboardLayout title="Injects">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 flex-col sm:flex-row flex-wrap gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search injects..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-injects"
              />
            </div>
            <Select value={exerciseFilter} onValueChange={setExerciseFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <Target className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by exercise" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Exercises</SelectItem>
                {exercises.map(ex => (
                  <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                    {ex.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {isController() && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleEmailReport}
                disabled={emailLoading}
                data-testid="email-inject-report-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {emailLoading ? 'Sending...' : 'Email Report'}
              </Button>
              <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="open-inject-import-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="open-inject-db-picker-btn">
                    <Database className="w-4 h-4 mr-2" />
                    From Database
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {exercises.map(ex => (
                    <DropdownMenuItem key={ex.exercise_id} onClick={() => { setDbPickerExerciseId(ex.exercise_id); setDbPickerOpen(true); }}>
                      <Target className="w-3 h-3 mr-2" />
                      {ex.name}
                    </DropdownMenuItem>
                  ))}
                  {exercises.length === 0 && (
                    <DropdownMenuItem disabled>No exercises available</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-inject-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Inject
              </Button>
            </div>
          )}
        </div>

        {/* Injects Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredInjects.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No injects found</p>
              <p className="text-muted-foreground">
                {searchQuery || exerciseFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create injects to add scenarios to your exercises'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto" data-testid="injects-table">
            <div className="min-w-[1100px] space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground border-b pb-2">
                <div className="col-span-1">ID</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1">Priority</div>
                <div className="col-span-2">Recipient</div>
                <div className="col-span-2">Title</div>
                <div className="col-span-2">Exercise</div>
                <div className="col-span-1">Time</div>
                <div className="col-span-2">Actions</div>
              </div>
              {filteredInjects.map((inject) => {
                const scheduled = getScheduledTime(inject);
                const displayTime = scheduled
                  ? scheduled
                  : inject.triggered_at
                    ? new Date(inject.triggered_at).toLocaleString()
                    : inject.created_at
                      ? new Date(inject.created_at).toLocaleString()
                      : '-';
                return (
                  <div
                    key={inject.inject_id}
                    className="grid grid-cols-12 gap-2 items-center rounded-lg border border-border/50 bg-card/50 p-3"
                    data-testid={`inject-row-${inject.inject_id}`}
                  >
                    <div className="col-span-1 text-xs text-muted-foreground">{getInjectIdLabel(inject)}</div>
                    <div className="col-span-1">
                      <Badge variant="outline" className={`${getStatusClass(inject.status)} capitalize`}>
                        {inject.status}
                      </Badge>
                    </div>
                    <div className="col-span-1">
                      <Badge variant="outline" className={`${getPriorityClass(inject.priority)} border capitalize`}>
                        {inject.priority}
                      </Badge>
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground truncate">{getRecipientLabel(inject)}</div>
                    <div className="col-span-2">
                      <p className="text-sm font-medium truncate">{inject.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{inject.content}</p>
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground truncate">
                      {exerciseMap[inject.exercise_id] || 'Unknown Exercise'}
                    </div>
                    <div className="col-span-1 text-xs text-muted-foreground">{displayTime}</div>
                    <div className="col-span-2 flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" onClick={() => openQuickView(inject)} data-testid={`inject-view-${inject.inject_id}`}>
                        View
                      </Button>
                      {isController() && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {inject.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleTriggerInject(inject.inject_id)}>
                                <Send className="w-4 h-4 mr-2" />
                                Play
                              </DropdownMenuItem>
                            )}
                            {inject.status === 'ongoing' && (
                              <DropdownMenuItem onClick={() => handleCompleteInject(inject.inject_id)}>
                                <Activity className="w-4 h-4 mr-2" />
                                Mark Completed
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openEditDialog(inject)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteInject(inject.inject_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Create Inject Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-secondary">Create Inject</DialogTitle>
              <DialogDescription>Add a new scenario inject</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateInject} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="exercise">Exercise *</Label>
                <Select 
                  value={formData.exercise_id}
                  onValueChange={(v) => setFormData({...formData, exercise_id: v})}
                >
                  <SelectTrigger data-testid="inject-exercise-select">
                    <SelectValue placeholder="Select exercise" />
                  </SelectTrigger>
                  <SelectContent>
                    {exercises.map(ex => (
                      <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                        {ex.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  data-testid="inject-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Inject Information *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={4}
                  required
                  data-testid="inject-content-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inject-time">Inject Time (UTC)</Label>
                  <Input
                    id="inject-time"
                    value={formData.inject_time}
                    onChange={(e) => setFormData({ ...formData, inject_time: e.target.value })}
                    placeholder="Oct 17th @ 10:00"
                    data-testid="inject-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inject-method">Method</Label>
                  <Input
                    id="inject-method"
                    value={formData.method}
                    onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                    data-testid="inject-method-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inject-dependancy">Dependancy</Label>
                  <Input
                    id="inject-dependancy"
                    value={formData.dependancy}
                    onChange={(e) => setFormData({ ...formData, dependancy: e.target.value })}
                    data-testid="inject-dependancy-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inject-recipient">Recipient</Label>
                  <Input
                    id="inject-recipient"
                    value={formData.recipient}
                    onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
                    data-testid="inject-recipient-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inject-specific-function">Specific Function</Label>
                  <Input
                    id="inject-specific-function"
                    value={formData.specific_function}
                    onChange={(e) => setFormData({ ...formData, specific_function: e.target.value })}
                    data-testid="inject-specific-function-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inject-reference">Reference (Objective ID)</Label>
                  <Input
                    id="inject-reference"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    data-testid="inject-reference-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inject-injected-by">Injected By</Label>
                  <Input
                    id="inject-injected-by"
                    value={formData.injected_by}
                    onChange={(e) => setFormData({ ...formData, injected_by: e.target.value })}
                    data-testid="inject-injected-by-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inject-objective">Objective</Label>
                <Textarea
                  id="inject-objective"
                  value={formData.objective}
                  onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                  rows={3}
                  data-testid="inject-objective-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inject-supplemental">Supplemental Information</Label>
                <Textarea
                  id="inject-supplemental"
                  value={formData.supplemental_information}
                  onChange={(e) => setFormData({ ...formData, supplemental_information: e.target.value })}
                  rows={3}
                  data-testid="inject-supplemental-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="inject-key-outcomes">Key Outcomes</Label>
                <Textarea
                  id="inject-key-outcomes"
                  value={formData.key_outcomes}
                  onChange={(e) => setFormData({ ...formData, key_outcomes: e.target.value })}
                  rows={3}
                  data-testid="inject-key-outcomes-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={formData.priority}
                  onValueChange={(v) => setFormData({...formData, priority: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="submit-inject-btn">
                  {formLoading ? 'Creating...' : 'Create Inject'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Inject Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-secondary">Edit Inject</DialogTitle>
              <DialogDescription>Update inject details and metadata</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateInject} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                  data-testid="edit-inject-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-content">Inject Information *</Label>
                <Textarea
                  id="edit-content"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={4}
                  required
                  data-testid="edit-inject-content-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-time">Inject Time (UTC)</Label>
                  <Input
                    id="edit-inject-time"
                    value={formData.inject_time}
                    onChange={(e) => setFormData({ ...formData, inject_time: e.target.value })}
                    placeholder="Oct 17th @ 10:00"
                    data-testid="edit-inject-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-method">Method</Label>
                  <Input
                    id="edit-inject-method"
                    value={formData.method}
                    onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                    data-testid="edit-inject-method-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-dependancy">Dependancy</Label>
                  <Input
                    id="edit-inject-dependancy"
                    value={formData.dependancy}
                    onChange={(e) => setFormData({ ...formData, dependancy: e.target.value })}
                    data-testid="edit-inject-dependancy-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-recipient">Recipient</Label>
                  <Input
                    id="edit-inject-recipient"
                    value={formData.recipient}
                    onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
                    data-testid="edit-inject-recipient-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-specific-function">Specific Function</Label>
                  <Input
                    id="edit-inject-specific-function"
                    value={formData.specific_function}
                    onChange={(e) => setFormData({ ...formData, specific_function: e.target.value })}
                    data-testid="edit-inject-specific-function-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-reference">Reference (Objective ID)</Label>
                  <Input
                    id="edit-inject-reference"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    data-testid="edit-inject-reference-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-inject-injected-by">Injected By</Label>
                  <Input
                    id="edit-inject-injected-by"
                    value={formData.injected_by}
                    onChange={(e) => setFormData({ ...formData, injected_by: e.target.value })}
                    data-testid="edit-inject-injected-by-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-inject-objective">Objective</Label>
                <Textarea
                  id="edit-inject-objective"
                  value={formData.objective}
                  onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                  rows={3}
                  data-testid="edit-inject-objective-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-inject-supplemental">Supplemental Information</Label>
                <Textarea
                  id="edit-inject-supplemental"
                  value={formData.supplemental_information}
                  onChange={(e) => setFormData({ ...formData, supplemental_information: e.target.value })}
                  rows={3}
                  data-testid="edit-inject-supplemental-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-inject-key-outcomes">Key Outcomes</Label>
                <Textarea
                  id="edit-inject-key-outcomes"
                  value={formData.key_outcomes}
                  onChange={(e) => setFormData({ ...formData, key_outcomes: e.target.value })}
                  rows={3}
                  data-testid="edit-inject-key-outcomes-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={formData.priority}
                  onValueChange={(v) => setFormData({...formData, priority: v})}
                >
                  <SelectTrigger data-testid="edit-inject-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="save-edit-inject-btn">
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary">Import Injects</DialogTitle>
              <DialogDescription>
                Upload a CSV/XLSX/XLSM/XLS file using your EMS template. Extra columns will be ignored and saved as metadata.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Select Exercise *</Label>
                <Select 
                  value={formData.exercise_id}
                  onValueChange={(v) => setFormData({...formData, exercise_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select exercise" />
                  </SelectTrigger>
                  <SelectContent>
                    {exercises.map(ex => (
                      <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                        {ex.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xlsm,.xls"
                  onChange={handleBulkImport}
                  disabled={!formData.exercise_id}
                  className="max-w-xs mx-auto"
                  data-testid="inject-import-file-input"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <InjectQuickViewModal
          open={quickViewOpen}
          onOpenChange={setQuickViewOpen}
          inject={quickViewInject}
          onOpenNotes={() => setQuickViewOpen(false)}
        />

        {/* Inject Database Picker */}
        {dbPickerExerciseId && (
          <InjectDatabasePicker
            exerciseId={dbPickerExerciseId}
            open={dbPickerOpen}
            onOpenChange={(open) => { setDbPickerOpen(open); if (!open) setDbPickerExerciseId(''); }}
            onAdded={loadData}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
